/**
 * UMD Entry Point for Yampa Widget SDK
 *
 * This file is the entry point for the UMD build that exposes the SDK
 * on `window.Yampa`. It handles both direct instantiation and the
 * async loader pattern with command queue replay.
 */

import type { WidgetOptions } from "./types";
// biome-ignore lint/style/noExportedImports: YampaWidgetImpl is used internally and YampaWidget is re-exported separately
import { YampaWidget as YampaWidgetImpl } from "./yampa-widget";

export {
  BridgeError,
  ConfigurationError,
  TimeoutError,
  ValidationError,
  YampaWidgetError,
} from "./errors";
export { EventEmitter } from "./events/event-emitter";
export type {
  EventHandler,
  EventMap,
  WidgetEvent,
} from "./events/types";
export type {
  AgentMessage,
  BubbleConfig,
  Conversation,
  Message,
  MessagePayload,
  UserMessage,
  WidgetConfig,
  WidgetMode,
  WidgetOptions,
  WidgetPosition,
  WidgetState,
} from "./types";

type QueuedCommand = [string, IArguments];

interface YampaStub {
  q?: QueuedCommand[];
  _config?: WidgetOptions;
  initialized?: boolean;
}

interface YampaGlobal extends YampaStub {
  _instance?: YampaWidgetImpl;
  init: (options: WidgetOptions) => YampaWidgetImpl;
  open: () => Promise<void>;
  close: () => Promise<void>;
  toggle: () => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  off: (event: string, handler: (...args: unknown[]) => void) => void;
  once: (event: string, handler: (...args: unknown[]) => void) => void;
  destroy: () => void;
  getState: () => ReturnType<YampaWidgetImpl["getState"]>;
  getConfig: () => ReturnType<YampaWidgetImpl["getConfig"]>;
  setCustomData: (data: Record<string, unknown>) => void;
  startConversation: () => Promise<string>;
  getMessages: () => ReturnType<YampaWidgetImpl["getMessages"]>;
  Widget: typeof YampaWidgetImpl;
}

declare global {
  interface Window {
    Yampa: YampaGlobal;
  }
}

/**
 * Initialize the Yampa Widget SDK
 */
function init(options: WidgetOptions): YampaWidgetImpl {
  if (window.Yampa._instance) {
    console.warn(
      "[Yampa] Widget already initialized. Call destroy() first to reinitialize."
    );
    return window.Yampa._instance;
  }

  const widget = new YampaWidgetImpl(options);
  window.Yampa._instance = widget;
  window.Yampa.initialized = true;

  return widget;
}

/**
 * Get the current widget instance, throwing if not initialized
 */
function getInstance(): YampaWidgetImpl {
  if (!window.Yampa._instance) {
    throw new Error("[Yampa] Widget not initialized. Call Yampa.init() first.");
  }
  return window.Yampa._instance;
}

/**
 * Replay queued commands from the async loader
 */
function replayQueue(queue: QueuedCommand[]): void {
  for (const [method, args] of queue) {
    try {
      const yampa = window.Yampa as unknown as Record<
        string,
        (...args: unknown[]) => unknown
      >;
      if (typeof yampa[method] === "function") {
        yampa[method](...Array.from(args));
      }
    } catch (error) {
      console.error(
        `[Yampa] Error replaying queued command "${method}":`,
        error
      );
    }
  }
}

/**
 * Set up the global Yampa object
 */
function setupGlobal(): void {
  const existingStub = window.Yampa as YampaStub | undefined;
  const queue = existingStub?.q || [];
  const pendingConfig = existingStub?._config;

  const yampa: YampaGlobal = {
    init,

    open: async () => getInstance().open(),
    close: async () => getInstance().close(),
    toggle: async () => getInstance().toggle(),
    sendMessage: async (message: string) => getInstance().sendMessage(message),
    on: (event: string, handler: (...args: unknown[]) => void) =>
      getInstance().on(
        event as Parameters<YampaWidgetImpl["on"]>[0],
        handler as Parameters<YampaWidgetImpl["on"]>[1]
      ),
    off: (event: string, handler: (...args: unknown[]) => void) =>
      getInstance().off(
        event as Parameters<YampaWidgetImpl["off"]>[0],
        handler as Parameters<YampaWidgetImpl["off"]>[1]
      ),
    once: (event: string, handler: (...args: unknown[]) => void) =>
      getInstance().once(
        event as Parameters<YampaWidgetImpl["once"]>[0],
        handler as Parameters<YampaWidgetImpl["once"]>[1]
      ),
    destroy: () => {
      if (window.Yampa._instance) {
        window.Yampa._instance.destroy();
        window.Yampa._instance = undefined;
        window.Yampa.initialized = false;
      }
    },
    getState: () => getInstance().getState(),
    getConfig: () => getInstance().getConfig(),
    setCustomData: (data: Record<string, unknown>) =>
      getInstance().setCustomData(data),
    startConversation: async () => getInstance().startConversation(),
    getMessages: async () => getInstance().getMessages(),

    Widget: YampaWidgetImpl,

    initialized: false,
  };

  window.Yampa = yampa;

  if (pendingConfig) {
    init(pendingConfig);
  }

  if (queue.length > 0) {
    replayQueue(queue);
  }
}

setupGlobal();

export type { YampaWidget as YampaWidgetType } from "./yampa-widget";
export { YampaWidget } from "./yampa-widget";
export default YampaWidgetImpl;
